import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import EventBusyIcon from '@mui/icons-material/EventBusy';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';

import { sendUpdateScheduleStatus } from 'state/schedules';
import { useAppDispatch, useAppSelector, useForceUpdate } from 'hooks';

import { Link as RouterLink } from 'react-router-dom';

import {
    round,
    getIconFromScheduleStatus,
    toTimeDDMonYearFormat,
    toReadableTimeFromNow,
    capitalize,
} from 'utils';
import { Schedule } from 'models/schedules';

interface Props {
    schedule: Schedule;
    onClick?: (schedule: Schedule) => void;
    /**
     * The context will indicate whether we bother displaying
     * the ground station name/details or the satellite name/details
     * generally because this will exist on a page
     */
    context: 'groundstation' | 'satellite';
}

const ScheduleSummary: React.FunctionComponent<Props> = ({
    schedule,
    context,
}) => {
    const theme = useTheme();
    const forceUpdate = useForceUpdate();

    const dispatch = useAppDispatch();
    const { scheduleStatusResponse } = useAppSelector((root) => root.schedules);

    const [updateRateFast, setUpdateRate] = useState(true);

    const { orbit, antenna } = schedule;

    const { satellite } = orbit ?? {};

    const { groundstation } = antenna;

    let button: React.ReactNode;

    const bookedStatus =
        schedule.status === 'booked' || schedule.status === 'auto_booked';
    const pendingStatus =
        schedule.status === 'to_confirm' || schedule.status === 'pending';

    useEffect(() => {
        const interval = setInterval(
            () => {
                forceUpdate();

                const time = Date.now();

                const startTime = schedule.start_time.getTime();
                const stopTime = schedule.stop_time.getTime();

                const shouldBeFast =
                    startTime - time < 90_000 ||
                    (time > startTime && time < stopTime) ||
                    time - stopTime < 90_000;

                setUpdateRate(shouldBeFast);
            },
            updateRateFast ? 1_000 : 30_000,
        );

        return () => {
            clearInterval(interval);
        };
    }, [updateRateFast, schedule]);

    const handleBookings = () => {
        let newStatus = schedule.status;

        if (pendingStatus) {
            newStatus = 'booked';
        }
        if (bookedStatus) {
            newStatus = 'pending';
        }

        dispatch(
            sendUpdateScheduleStatus({ id: schedule.id, status: newStatus }),
        );
    };

    if (bookedStatus) {
        button = (
            <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<EventBusyIcon />}
                onClick={handleBookings}
                disabled={
                    //stops the user from clicking on the button if loading, and disables button if communication has started (or on pending passes, has been ignored)
                    scheduleStatusResponse.status === 'loading' ||
                    schedule.start_time.getTime() <= new Date().getTime()
                }
            >
                Unbook
            </Button>
        );
    } else if (pendingStatus) {
        button = (
            <Button
                variant="outlined"
                color="success"
                size="small"
                startIcon={<EventAvailableIcon />}
                onClick={handleBookings}
                disabled={
                    scheduleStatusResponse.status === 'loading' ||
                    schedule.start_time.getTime() <= new Date().getTime()
                }
            >
                Book
            </Button>
        );
    }

    const now = new Date();
    const timeInfo = (
        <Typography variant="subtitle1">
            {now < schedule.start_time &&
                `Starting ${toReadableTimeFromNow(schedule.start_time)}`}
            {now > schedule.start_time &&
                now < schedule.stop_time &&
                `Ending ${toReadableTimeFromNow(schedule.stop_time)}`}
            {now > schedule.stop_time &&
                `Ended ${toReadableTimeFromNow(schedule.stop_time)}`}
        </Typography>
    );

    return (
        <Box
            sx={{
                display: 'grid',
                background: theme.palette.background.details,
                px: 1,
                borderRadius: 1,
                maxWidth: 600,
                minWidth: 400,
                breakInside: 'avoid',
            }}
        >
            {context === 'groundstation' && (
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                alignItems: 'center',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    alignItems: 'center',
                                }}
                            >
                                {getIconFromScheduleStatus(schedule.status)}
                                <Typography variant="h6">
                                    {satellite?.name}
                                </Typography>
                                <Typography variant="subtitle1">
                                    ({satellite?.catalogue_number})
                                </Typography>
                            </Box>
                        </Box>
                        {timeInfo}
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography variant="subtitle1">
                            <b>#{schedule.id}</b>
                        </Typography>
                        <Typography variant="subtitle1">
                            Satellite Owner: {satellite?.organisation.name}
                        </Typography>
                        <Typography variant="subtitle1">
                            {toTimeDDMonYearFormat(schedule.start_time)}
                        </Typography>
                    </Box>
                </>
            )}
            {context === 'satellite' && (
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1,
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                alignItems: 'center',
                            }}
                        >
                            <Typography variant="h6">
                                {groundstation.name}
                            </Typography>
                            <Typography variant="subtitle1">
                                ({antenna.name})
                            </Typography>
                        </Box>

                        <Typography variant="subtitle1">{timeInfo}</Typography>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography variant="subtitle1">
                            <b>#{schedule.id}</b>
                        </Typography>
                        <Typography variant="subtitle1">
                            Antenna Owner: {groundstation?.organisation.name}
                        </Typography>
                        <Typography variant="subtitle1">
                            Start Time:{' '}
                            {toTimeDDMonYearFormat(schedule.start_time)}
                        </Typography>
                    </Box>
                </>
            )}

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Typography>Status: {capitalize(schedule.status)}</Typography>
                <Typography>Duration: {round(schedule.duration)}s</Typography>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingBottom: 1,
                }}
            >
                {/* Disabling this Button as the Page is goes to is incomplete */}
                {false && (
                    <IconButton
                        size="small"
                        color="primary"
                        component={RouterLink}
                        to={`/schedule/${schedule.id}`}
                    >
                        <SettingsInputAntennaIcon color="success" />
                    </IconButton>
                )}
                <Box />

                {button}
            </Box>
        </Box>
    );
};

ScheduleSummary.displayName = 'ScheduleSummary';
export default ScheduleSummary;
